import {
	createContext,
	useContext,
	useState,
	useEffect, useRef
} from "@wordpress/element";
import { getImageHubSettings } from "./useSettings.js";
import { PROVIDERS } from "../helpers.js";

const server_url = image_hub_data.server_url;

export const PER_PAGE = 30;
const defaultPagination = {
	currentPage: 1,
	totalPages: 1,
	totalResults: 0,
	hasNextPage: false,
	nextPage: false,
};
const ImageContext = createContext();

export const ImageProvider = ({ children }) => {
	const [images, setImages] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [lastSearchTerm, setLastSearchTerm] = useState("");
	const [provider, setProvider] = useState("All"); // Set initial provider to "All"
	const [errors, setErrors] = useState("All");
	const [additionalQueryParams, setAdditionalQueryParams] = useState({});
	const [pagination, setPagination] = useState(defaultPagination);
	const [isLoading, setIsLoading] = useState(true);
	const { imageHubSettings, apiKeys } = getImageHubSettings();
	let [refresh, setRefresh] = useState(false)

	useEffect(() => {
		if (images.length ) {
			sessionStorage.setItem("image_hub_session", JSON.stringify({
				images,
				searchTerm: lastSearchTerm,
				pagination,
				provider,
				additionalQueryParams
			}));
		}
	}, [images, searchTerm, pagination, provider, additionalQueryParams, lastSearchTerm]);

	useEffect(() => {
		const saved = sessionStorage.getItem("image_hub_session");
		if (saved) {
			try {
				//dont know yet if saving all images json is correct , but keeps history consistent 
				const parsed = JSON.parse(saved);
				if (parsed.images) setImages(parsed.images);
				if (parsed.searchTerm) {
					setLastSearchTerm(parsed.searchTerm);
					setSearchTerm(parsed.searchTerm);
				}
				if (parsed.provider) setProvider(parsed.provider);
				if (parsed.additionalQueryParams) setAdditionalQueryParams(parsed.additionalQueryParams);
				if (parsed.pagination) setPagination(parsed.pagination);
				setIsLoading(false)
			} catch (e) {
				console.error("Failed to parse session storage:", e);
			}
		} else {
			fetchImages(searchTerm?.toLowerCase(), provider, 1, true);
		}
	}, []);

	const removeImage = (id) => {
		setImages((prevImgs) => {
			return prevImgs.filter((x) => x.id !== id);
		});
		setPagination(prevPagination => ({
			...prevPagination,
			totalResults: prevPagination.totalResults - 1,
		}))
	};
	const updateQueryParams = (key, value) => {

		setAdditionalQueryParams((prev) => {
			let clonn = { ...prev }
			if (value == "All" || value == "") {
				setRefresh(true)
				delete clonn[key]
			} else {
				clonn[key] = value
			}

			return clonn
		});

	};

	// Function to fetch images from the API
	const fetchImages = (
		query = searchTerm,
		selectedProvider = provider,
		page = pagination.currentPage,
		reset = false,
		additional_query_params = additionalQueryParams
	) => {
		setIsLoading(true);
		if (reset) {
			setPagination(defaultPagination);
			setImages([]);
		}
		setLastSearchTerm(query);
		const enabledProviders = Object.keys(PROVIDERS).filter(
			(key) =>
				key !== "All" &&
				imageHubSettings[`image_hub_enable_${PROVIDERS[key].name}`],
		);
		//due to tos
		const enabledProvidersWithoutGiphy = enabledProviders.filter(
			(x) => x != "GIPHY",
		);
		console.log({enabledProviders,enabledProvidersWithoutGiphy})
		const providerParam =
			selectedProvider === "All"
				? `&provider=${enabledProvidersWithoutGiphy.map((p) => PROVIDERS[p].name).join(",")}`
				: `&provider=${selectedProvider}`;
		const additionalParams =
			selectedProvider !== "All"
				? Object.entries(additional_query_params)
						.map(([key, value]) => {
							return `&${key?.toLowerCase()}=${encodeURIComponent(value?.toLowerCase())}`;
						})
						.join("")
				: "";
		const apiUrl = `${server_url}/?query=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}${additionalParams}${providerParam}`;
		const headers = {};

		if (!apiKeys.image_hub_use_default_keys) {
			Object.entries(apiKeys || {}).forEach(([key, value]) => {
				if (value) {
					const headerKey = key.replace(/_/g, "-"); // Convert underscores to hyphens
					headers[headerKey] = value;
				}
			});
		}

		return fetch(apiUrl, {
			method: "GET",
			headers: headers,
		})
			.then((response) => response.json())
			.then((data) => {
				if (reset) {
					setPagination(defaultPagination);
					setImages([]);
				}
				setImages((x) => [...x, ...(data.images || [])]);
				setPagination(data.pagination);
				setIsLoading(false);
				setErrors(data.errors);
				return data.images;
			})
			.catch((error) => {
				if (reset) {
					setPagination(defaultPagination);
					setImages([]);
				}
				setErrors({ All: error.message });
				console.error("Error fetching images:", { error });
				setIsLoading(false);
				return [];
			});
	};

	// Function to handle the search
	const handleSearch = debounce((param) => {
		fetchImages(
			param ?? searchTerm,
			provider,
			1,
			true,
		);
	}, 500);

	useEffect(() => {
		if ((Object.keys(additionalQueryParams).length > 0) || refresh) {
			refresh = false;
			handleSearch();
		}
	}, [additionalQueryParams]);

	const value = {
		errors,
		lastSearchTerm,
		images,
		searchTerm,
		setSearchTerm,
		provider,
		setProvider,
		fetchImages,
		handleSearch,
		pagination,
		isLoading,
		additionalQueryParams,
		updateQueryParams,
		removeImage,
		setAdditionalQueryParams
	};

	return (
		<ImageContext.Provider value={value}>{children}</ImageContext.Provider>
	);
};

export const useImageContext = () => {
	const context = useContext(ImageContext);
	if (context === undefined) {
		throw new Error("useImageContext must be used within an ImageProvider");
	}
	return context;
};



let timeoutId;
const debounce = (func, delay) => {
	return (...args) => {
		// Clear previous timeout
		if (timeoutId) {
			clearTimeout(timeoutId);
		}

		// Set a new timeout
		timeoutId = setTimeout(() => {
			func(...args);
		}, delay);
	};
};